import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Grid,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { request } from '../../Utils/request';
import PlaceCard from './PlaceCard';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import { calculateDistance } from '../../Utils/googleMapsDistance';

interface Place {
  _id: string;
  name: string;
  description: string;
  categories: string[];
  tags: string[];
  type: string;
  featured: boolean;
  location: {
    type: string;
    coordinates?: [number, number];
    lat?: number;
    lng?: number;
    zoom: number;
  };
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    zipcode: string;
  };
  owner_details: {
    name: string;
    email: string;
    phone: string;
  };
  files: Array<{
    fileURL: string;
    fileType: string;
  }>;
  rating?: number;
  distance?: string;
  distanceValue?: number;
  createdAt: string;
  updatedAt: string;
  isLiked: boolean;
}

interface FavoritePlacesResponse {
  success: boolean;
  data: Place[];
  total: number;
}

const FavoritePlaces: React.FC = () => {
  const navigate = useNavigate();
  const [places, setPlaces] = useState<Place[]>([]);
  const [placesWithDistance, setPlacesWithDistance] = useState<Place[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const userLocation = useSelector((state: RootState) => state.location.location);

  useEffect(() => {
    fetchFavoritePlaces();
  }, []);

  const fetchFavoritePlaces = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await request<FavoritePlacesResponse>('/places/liked-places', {
        method: 'GET',
        authorization: true,
      });

      if (response.success === true) {
        setPlaces(response.data);
      } else {
        setError('Failed to fetch favorite places');
      }
    } catch (err) {
      console.error('Error fetching favorite places:', err);
      setError('Failed to load favorite places. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const computeDistances = async () => {
      if (!places.length) {
        if (isMounted) setPlacesWithDistance(places);
        return;
      }

      if (!userLocation?.lat || !userLocation?.lng) {
        if (isMounted) setPlacesWithDistance(places);
        return;
      }

      try {
        const updatedPlaces = await Promise.all(
          places.map(async (place) => {
            const placeLat =
              place.location?.coordinates && place.location.coordinates.length >= 2
                ? place.location.coordinates[1]
                : place.location?.lat;
            const placeLng =
              place.location?.coordinates && place.location.coordinates.length >= 2
                ? place.location.coordinates[0]
                : place.location?.lng;

            if (placeLat == null || placeLng == null) {
              return { ...place, distance: 'N/A', distanceValue: Infinity };
            }

            try {
              const distance = await calculateDistance(
                userLocation.lat,
                userLocation.lng,
                placeLat,
                placeLng
              );
              return {
                ...place,
                distance: `${distance.toFixed(1)}km`,
                distanceValue: distance,
              };
            } catch (err) {
              console.warn(`Failed to compute distance for ${place.name}`, err);
              return { ...place, distance: 'N/A', distanceValue: Infinity };
            }
          })
        );

        const sortedPlaces = updatedPlaces.sort(
          (a, b) => (a.distanceValue ?? Infinity) - (b.distanceValue ?? Infinity)
        );

        if (isMounted) setPlacesWithDistance(sortedPlaces);
      } catch (err) {
        console.error('Error computing distances for favorite places:', err);
        if (isMounted) setPlacesWithDistance(places);
      }
    };

    computeDistances();

    return () => {
      isMounted = false;
    };
  }, [places, userLocation]);

  const handleFavoriteToggle = (placeId: string) => {
    setPlaces((prev) => prev.filter((place) => place._id !== placeId));
  };

  const placesToRender = placesWithDistance.length ? placesWithDistance : places;

  const handlePlaceClick = (placeId: string) => {
    navigate(`/places/${placeId}`);
  };

  if (loading) {
    return (
      <Box sx={{ 
        bgcolor: '#fff', 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <TopNavBar title="Favorite Places" canGoBack />
        <Container maxWidth="sm" sx={{ flex: 1, py: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        bgcolor: '#fff', 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <TopNavBar title="Favorite Places" canGoBack />
        <Container maxWidth="sm" sx={{ flex: 1, py: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: '#fff', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <TopNavBar title="Favorite Places" canGoBack />
      
      <Container maxWidth="sm" sx={{ flex: 1, py: 2 }}>
        {placesToRender?.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: '50vh',
            textAlign: 'center'
          }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No Favorite Places
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Places you like will appear here
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Your Favorite Places ({placesToRender.length})
            </Typography>
            
            <Grid container spacing={2}>
              {placesToRender.map((place) => (
                <Grid sx={{width: '100%'}} key={place._id}>
                  <PlaceCard
                    imageUrl={place.files?.[0]?.fileURL || '/placeholder-image.jpg'}
                    title={place.name}
                    distance={place.distance || 'N/A'}
                    category={place.categories || ['Sights']}
                    tags={place.tags || []}
                    rating={place.rating || 0}
                    islistview={true}
                    id={place._id}
                    isFavorited
                    isLiked={place.isLiked}
                    onFavoriteToggle={handleFavoriteToggle}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Container>
    </Box>
  );
};

export default FavoritePlaces;
